import { useEffect, useState } from "react";
import axios from "axios";
import { Lock, LogOut, CheckCircle2, Clock, AlertCircle } from "lucide-react";
import { PageMeta } from "@/components/site/PageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API, withCredentials: true });

const fmtDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const formatError = (detail) => {
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
    return "Something went wrong. Please try again.";
};

const StatusChip = ({ status }) => {
    const styles = {
        paid: "border-blue-500/50 text-blue-400",
        processing: "border-amber-500/70 text-amber-500",
        pending: "border-slate-700 text-slate-400",
        failed: "border-red-600/50 text-red-400",
        expired: "border-slate-700 text-slate-500",
        refunded: "border-slate-700 text-slate-500",
    };
    return (
        <span className={`inline-block border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] ${styles[status] || styles.pending}`}>
            {status}
        </span>
    );
};

const inputClass =
    "w-full border border-slate-800 bg-transparent px-4 py-3 text-sm text-slate-200 placeholder-slate-600 outline-none transition-colors duration-300 focus:border-blue-500";

export default function Admin() {
    const [auth, setAuth] = useState(null);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [sending, setSending] = useState(false);
    const [inquiries, setInquiries] = useState([]);
    const [payments, setPayments] = useState([]);

    const loadData = async () => {
        const [inq, pay] = await Promise.all([
            api.get("/admin/inquiries"),
            api.get("/admin/payments"),
        ]);
        setInquiries(inq.data);
        setPayments(pay.data);
    };

    useEffect(() => {
        api.get("/auth/me")
            .then(() => {
                setAuth(true);
                loadData();
            })
            .catch(() => setAuth(false));
    }, []);

    const login = async (e) => {
        e.preventDefault();
        setSending(true);
        setError("");
        try {
            await api.post("/auth/login", { email, password });
            setAuth(true);
            await loadData();
        } catch (err) {
            setError(formatError(err.response?.data?.detail));
        } finally {
            setSending(false);
        }
    };

    const logout = async () => {
        await api.post("/auth/logout");
        setAuth(false);
        setInquiries([]);
        setPayments([]);
    };

    const markHandled = async (id) => {
        await api.post(`/admin/inquiries/${id}/handled`);
        setInquiries((list) => list.map((i) => (i.id === id ? { ...i, handled: true } : i)));
    };

    if (auth === null) {
        return (
            <section className="flex min-h-[60vh] items-center justify-center pt-32">
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-slate-500">Loading…</p>
            </section>
        );
    }

    if (!auth) {
        return (
            <>
                <PageMeta title="Owner Sign In — Nationwide Strategies LLC" description="Private owner access." />
                <section data-testid="admin-login" className="flex min-h-[70vh] items-center pt-32 sm:pt-40">
                    <div className="mx-auto w-full max-w-md px-4 pb-24 sm:px-6">
                        <div className="flex items-center gap-3">
                            <Lock size={16} className="text-blue-400" aria-hidden="true" />
                            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-blue-400">Owner Access</p>
                        </div>
                        <h1 className="mt-5 font-serif text-4xl font-medium tracking-tight text-slate-100">Private dashboard.</h1>
                        <form onSubmit={login} className="mt-10 border border-slate-800 bg-[#0b0f16] p-8">
                            <label htmlFor="admin-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Email</label>
                            <input id="admin-email" data-testid="admin-email-input" type="email" required value={email}
                                onChange={(e) => setEmail(e.target.value)} className={inputClass} autoComplete="username" />
                            <label htmlFor="admin-password" className="mb-2 mt-6 block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Password</label>
                            <input id="admin-password" data-testid="admin-password-input" type="password" required value={password}
                                onChange={(e) => setPassword(e.target.value)} className={inputClass} autoComplete="current-password" />
                            <button type="submit" data-testid="admin-login-button" disabled={sending}
                                className="mt-8 w-full bg-slate-100 px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-900 transition-colors duration-300 hover:bg-white disabled:opacity-50">
                                {sending ? "Signing In…" : "Sign In"}
                            </button>
                            {error && <p data-testid="admin-login-error" role="alert" className="mt-4 text-sm text-red-400">{error}</p>}
                        </form>
                    </div>
                </section>
            </>
        );
    }

    return (
        <>
            <PageMeta title="Owner Dashboard — Nationwide Strategies LLC" description="Private owner dashboard." />
            <section data-testid="admin-dashboard" className="pt-32 sm:pt-40">
                <div className="mx-auto max-w-5xl px-4 pb-28 sm:px-6">
                    <div className="flex flex-wrap items-end justify-between gap-6">
                        <div>
                            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-blue-400">Owner Dashboard</p>
                            <h1 className="mt-4 font-serif text-4xl font-medium tracking-tight text-slate-100">Inquiries & payments.</h1>
                        </div>
                        <button onClick={logout} data-testid="admin-logout-button"
                            className="inline-flex items-center gap-2 border border-slate-700 px-5 py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-300 transition-colors duration-300 hover:border-blue-500 hover:text-white">
                            <LogOut size={13} aria-hidden="true" /> Sign Out
                        </button>
                    </div>

                    <div className="mt-14">
                        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-slate-500">
                            Inquiries · {inquiries.length}
                        </p>
                        <div className="mt-6 border border-slate-800">
                            {inquiries.length === 0 && (
                                <p className="p-8 text-sm text-slate-500">No inquiries yet.</p>
                            )}
                            {inquiries.map((inq, i) => (
                                <div key={inq.id} data-testid={`admin-inquiry-row-${i + 1}`}
                                    className={`border-t border-slate-800 bg-[#0b0f16] p-6 first:border-t-0 sm:p-8 ${inq.handled ? "opacity-50" : ""}`}>
                                    <div className="flex flex-wrap items-baseline justify-between gap-3">
                                        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                                            <span className="font-serif text-xl text-slate-100">{inq.name}</span>
                                            {inq.organization && <span className="text-sm text-slate-400">{inq.organization}</span>}
                                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-blue-400">{inq.matter}</span>
                                        </div>
                                        <span className="font-mono text-[11px] tracking-[0.15em] text-slate-500">{fmtDate(inq.created_at)}</span>
                                    </div>
                                    <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-400">{inq.message}</p>
                                    <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-500">
                                        <span className="font-mono">{inq.email}</span>
                                        {inq.phone && <span className="font-mono">{inq.phone}</span>}
                                        {!inq.handled && (
                                            <button onClick={() => markHandled(inq.id)} data-testid={`admin-mark-handled-${i + 1}`}
                                                className="inline-flex items-center gap-2 border border-slate-700 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-300 transition-colors duration-300 hover:border-blue-500 hover:text-white">
                                                <CheckCircle2 size={12} aria-hidden="true" /> Mark handled
                                            </button>
                                        )}
                                        {inq.handled && (
                                            <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                                <CheckCircle2 size={12} aria-hidden="true" /> Handled
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="mt-16">
                        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-slate-500">
                            Payments · {payments.length}
                        </p>
                        <div className="mt-6 border border-slate-800">
                            {payments.length === 0 && (
                                <p className="p-8 text-sm text-slate-500">No payments yet.</p>
                            )}
                            {payments.map((p, i) => (
                                <div key={p.reference + i} data-testid={`admin-payment-row-${i + 1}`}
                                    className="grid items-baseline gap-2 border-t border-slate-800 bg-[#0b0f16] p-6 first:border-t-0 sm:grid-cols-12 sm:gap-6 sm:p-8">
                                    <span className="font-mono text-sm text-slate-200 sm:col-span-3">{p.invoice_number}</span>
                                    <span className="font-serif text-xl text-slate-100 sm:col-span-3">
                                        ${Number(p.amount).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                                    </span>
                                    <span className="font-mono text-[11px] tracking-[0.15em] text-slate-500 sm:col-span-3">{fmtDate(p.created_at)}</span>
                                    <span className="sm:col-span-3 sm:text-right"><StatusChip status={p.payment_status} /></span>
                                </div>
                            ))}
                        </div>
                        <p className="mt-4 flex items-center gap-2 text-xs text-slate-500">
                            <Clock size={12} aria-hidden="true" /> Bank (ACH) transfers show as processing until they clear.
                            <AlertCircle size={12} aria-hidden="true" className="hidden" />
                        </p>
                    </div>
                </div>
            </section>
        </>
    );
}
